;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Form,
  Label,
  Input,
  FormGroup,
  InputProps,
  FormFeedback,
  FormText,
} from 'reactstrap';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { useParams } from 'next/navigation';
import usePhoneInputLabelLanguage from '../hooks/usePhoneInputLabelLanguage';
import validateMail from '../utils/validateEmailAddress';
import Cta from './CtaBtn';
import { event } from '../utils/gtag';
import { plausibleEvent } from '../utils/plausibleEvent';
import axiosInstance from '../utils/axios';

import 'react-phone-number-input/style.css';
import { Dictionaries } from '../i18n/i18n-config';
import { getI18nUtils } from '../i18n/i18n-utils';

type DynamicInput = {
  type: InputProps['type'];
  value: any;
  touched: boolean;
  valid: boolean;
  required: boolean;
  intlId?: string;
  order: number;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  options?: any;
  allowedFileTypes?: string[];
  error: string | null;
  title?: string;
  description?: string;
  isCustomField?: boolean;
};

function RegistrationForm({
  id,
  source,
  content = {},
  dictionary,
}: {
  id: string;
  source: 'landingPage' | 'event' | 'jobPage' | 'blogPost' | 'jumpToSynergieConsultants' | 'jumpToSynergieManagers';
  content?: { fields?: any; cvUpload?: boolean; title?: string; confirmationMessage?: string };
  dictionary: Partial<Dictionaries>;
}): JSX.Element {
  const { fields: customFields, cvUpload, title, confirmationMessage } = content;
  const phoneLabelLanguage = usePhoneInputLabelLanguage();
  const showCvUpload = typeof cvUpload === 'boolean' ? cvUpload : true;
  function generateInputs() {
    const newInputs: Record<string, DynamicInput> = {
      firstName: {
        type: 'text',
        value: '',
        touched: false,
        valid: false,
        minLength: 2,
        intlId: 'registration-form-firstName',
        order: 1,
        required: true,
        error: null,
      },
      lastName: {
        type: 'text',
        value: '',
        touched: false,
        valid: false,
        minLength: 2,
        intlId: 'registration-form-lastName',
        order: 2,
        required: true,
        error: null,
      },
      telephone: {
        type: 'tel',
        value: '',
        touched: false,
        valid: false,
        intlId: 'registration-form-phone',
        order: 3,
        required: true,
        error: null,
      },
      email: {
        type: 'email',
        value: '',
        touched: false,
        valid: false,
        intlId: 'registration-form-email',
        order: 4,
        required: true,
        error: null,
      },
    };

    if (showCvUpload) {
      newInputs.cv = {
        type: 'file',
        value: '',
        intlId: 'CV',
        touched: false,
        valid: true,
        required: false,
        error: null,
        order: 5,
        allowedFileTypes: ['.doc', '.docx', '.pdf', '.rtf'],
      };
    }
    if (customFields) {
      customFields.forEach((field, index) => {
        const key = `customField_${field.title}`;
        newInputs[key] = {
          type: field.type,
          title: field.title,
          description: field.description,
          value: '',
          touched: false,
          valid: false,
          required: false,
          error: null,
          isCustomField: true,
          order: index + 6,
          options: field.selectOptions?.options,
        };
      });
    }

    return newInputs;
  }

  const [inputs, setInputs] = useState(generateInputs());
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const { formatMessage } = getI18nUtils(dictionary);
  const { locale } = useParams();
  const app = process.env.NEXT_PUBLIC_APP;

  // validate a specific input
  function isInputValid(input) {
    const { value, type, minLength, maxLength, required } = input;
    if (value === '' && required) return false;

    if (minLength && value.length < minLength) return false;

    if (maxLength && value.length > maxLength) return false;

    if (type === 'email' && !validateMail(value)) return false;

    if (type === 'tel' && !isValidPhoneNumber(value)) return false;

    return true;
  }

  async function onSubmitHandler(e) {
    e.preventDefault();
    setErrorMessage(null);
    setSubmitting(true);
    // Extract basic input fields
    const { firstName, lastName, telephone, email, cv } = inputs;

    // Build request body from basic fields
    const body: any = {
      id,
      eventType: source,
      firstName: firstName.value,
      lastName: lastName.value,
      telephone: telephone.value,
      email: email.value,
      language: locale,
      app: app || 'synergiejobs.be',
    };

    // Custom fields can be added to the a form in Start
    // They're identified by the `isCustomField` property
    type CustomField = {
      title?: string;
      type?: string;
      description?: string;
      value: string;
    };
    const fields: CustomField[] = Object.keys(inputs)
      .filter((key) => inputs[key].isCustomField) // Skip non-custom fields
      .filter((key) => inputs[key].value) // Skip empty fields
      .map((field) => {
        const fieldData = inputs[field];
        return {
          title: fieldData.title,
          type: fieldData.type,
          description: fieldData.description,
          value: fieldData.value,
        };
      });
    if (fields.length > 0) body.fields = fields;

    // Add CV to request body
    if (cv && cv.value && cv.value.name && cv.value.file) body.cv = { name: cv.value.name, file: cv.value.file };

    await axiosInstance.post(`/registrations`, body);

    setSubmitting(false);

    setCompleted(true);
    setErrorMessage(null);
    plausibleEvent('registrationFormSubmit', { type: body.eventType });

    event({
      action: `${body.eventType}RegistrationFormSubmit`,
      category: app || 'synergiejobs.be',
      label: '',
      value: body.id,
    });
  }

  // The form is valid when all fields are valid and the terms are accepted
  function checkFormValidity() {
    const validForm = Object.keys(inputs).every((input) => inputs[input].valid) && termsAccepted;
    setFormIsValid(validForm);
    return validForm;
  }

  // validate the form when an input changes
  useEffect(() => {
    checkFormValidity();
  }, [inputs, termsAccepted, errorMessage]);

  function inputChangedHandler(e: any, inputKey: string) {
    setInputs((prev) => {
      const inputValue = inputKey === 'telephone' ? e : e.target.value;
      const inputCp: any = { ...prev[inputKey], touched: true, value: inputValue };
      if (inputCp.type === 'file') {
        const file = e.target.files[0];
        const extension = file ? file.name.substring(file.name.lastIndexOf('.')) : null;

        if (file && inputCp.allowedFileTypes && inputCp.allowedFileTypes.indexOf(extension.toLowerCase()) !== -1) {
          inputCp.valid = true;
          const Reader = new FileReader();
          Reader.onload = (readerEvent) => {
            inputCp.value = { file: readerEvent.target?.result, size: file.size, name: file.name };
          };
          Reader.readAsDataURL(file);
        } else {
          inputCp.valid = false;
          inputCp.error = formatMessage({ id: 'file-extension-not-allowed-for-upload' });
        }
      } else inputCp.valid = isInputValid(inputCp);

      return { ...prev, [inputKey]: inputCp };
    });
    checkFormValidity();
  }

  const sortedFields = Object.keys(inputs).sort((a, b) => (inputs[a].order > inputs[b].order ? 1 : -1));

  function buildInputElement(input: DynamicInput, inputKey: string) {
    switch (input.type) {
      // Select Elements are rendered as a list of radio buttons
      case 'select':
        return (
          <FormGroup tag="fieldset" className="mt-2" key={inputKey}>
            {input.options.map((option) => (
              <Col xs={12} key={option}>
                <FormGroup check>
                  <Label check>
                    <Input
                      name={inputKey}
                      type="radio"
                      value={option.value}
                      onBlur={() => checkFormValidity()}
                      onChange={(e) => inputChangedHandler(e, inputKey)}
                    />
                    {option.value}
                  </Label>
                </FormGroup>
              </Col>
            ))}
          </FormGroup>
        );
      case 'file':
        return (
          <Input
            name={inputKey}
            id={inputKey}
            required={input.required}
            type={input.type}
            valid={input.touched && input.valid}
            invalid={input.touched && !input.valid}
            className="mb-3"
            onChange={(e) => inputChangedHandler(e, inputKey)}
            onBlur={() => checkFormValidity()}
            placeholder={input.placeholder ? formatMessage({ id: input.placeholder }) : ''}
            accept={input.allowedFileTypes ? input.allowedFileTypes.join(',') : ''}
          />
        );
      case 'tel':
        return (
          <PhoneInput
            name={inputKey}
            id={inputKey}
            value={input.value}
            required={input.required}
            type={input.type}
            defaultCountry="BE"
            labels={phoneLabelLanguage}
            international
            countryCallingCodeEditable={false}
            className={`mb-3 ${input.touched && input.valid ? 'is-valid' : ''} ${
              input.touched && !input.valid ? 'is-invalid' : ''
            }`}
            // value is stored directly in 'e' so 'e' can be undefined
            onChange={(e) => e && inputChangedHandler(e, inputKey)}
            onBlur={() => checkFormValidity()}
            placeholder={input.placeholder ? formatMessage({ id: input.placeholder }) : ''}
          />
        );
      default:
        return (
          <Input
            name={inputKey}
            id={inputKey}
            value={input.value}
            minLength={input.minLength}
            maxLength={input.maxLength}
            required={input.required}
            type={input.type}
            valid={input.touched && input.valid}
            invalid={input.touched && !input.valid}
            className="mb-3"
            onChange={(e) => inputChangedHandler(e, inputKey)}
            onBlur={() => checkFormValidity()}
            placeholder={input.placeholder ? formatMessage({ id: input.placeholder }) : ''}
          />
        );
    }
  }

  return (
    <Card id="registrationForm">
      <CardBody>
        {!completed ? (
          <>
            <CardTitle tag="h2" className="mb-4">
              {title || formatMessage({ id: 'registration-form-title' })}
            </CardTitle>
            <Form onSubmit={(e) => onSubmitHandler(e)}>
              <fieldset disabled={submitting} aria-busy={submitting}>
                <Row>
                  {sortedFields.map((inputKey) => {
                    const { intlId, required, error, isCustomField, title: inputTitle, description } = inputs[inputKey];
                    return (
                      <Col md={isCustomField ? 12 : 6} key={`registrationForm-col-${inputKey}`}>
                        <FormGroup key={inputKey}>
                          <Label className={`text-muted${required ? ' required' : ''}`} for={inputKey}>
                            {inputTitle || (intlId && formatMessage({ id: intlId })) || ''}
                          </Label>
                          {buildInputElement(inputs[inputKey], inputKey)}
                          {description && <FormText dangerouslySetInnerHTML={{ __html: description }} />}
                          {error && <FormFeedback>{error}</FormFeedback>}
                        </FormGroup>
                      </Col>
                    );
                  })}
                </Row>

                <Row className="mt-3 ">
                  <Col xs={12}>
                    <FormGroup check inline>
                      <Input
                        type="checkbox"
                        id="terms"
                        name="terms"
                        onChange={() => {
                          setTermsAccepted(!termsAccepted);
                        }}
                        checked={termsAccepted}
                        className="me-0"
                      />
                      <Label for="terms" check>
                        {formatMessage(
                          { id: 'accept-privacy-terms' },
                          {
                            privacyLink: (
                              <a href={`/${locale}/privacy`} target="_blank" rel="noopener noreferrer">
                                {formatMessage({ id: 'privacy_policy' })}
                              </a>
                            ),
                            termsLink: (
                              <a href={`/${locale}/termsandconditions`} target="_blank" rel="noopener noreferrer">
                                {formatMessage({ id: 'termsandconditions' })}
                              </a>
                            ),
                          }
                        )}
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                <Cta className="cta red noArrow" type="submit" disabled={!formIsValid}>
                  {formatMessage({ id: 'registration-form-submit' })}
                </Cta>
              </fieldset>
            </Form>
          </>
        ) : (
          <p className="my-3">{confirmationMessage || formatMessage({ id: 'registration-form-thanks' })}</p>
        )}
      </CardBody>
    </Card>
  );
}

export default RegistrationForm;
